import { isEmpty } from 'lodash';
import { useMemo } from 'react';
import { MBText } from '@shared/components/MBText';
import { MBView } from '@shared/components/MBView';
import { View } from '@shared/components/View';
import { MBTextOverlineBold } from '@shared/components/typography';
import { Colors } from '@shared/styles';
import { API } from '@shared/types/api';
import { MBReactionButtonWithPicker } from './buttonWithPicker';
import { MBReactionButtonWithPickerProps } from './buttonWithPicker/props';
import { Styles } from './styles';
import { getReactionEmoji } from './types';

export interface MBStackedReactionsProps
  extends Omit<MBReactionButtonWithPickerProps, 'anchorName'> {
  messageId: number;
  // List of all current reactions users have made.
  reactions: API.ReactionFragment[];
  align: 'left' | 'right';
  anchorName?: string;
}

/*
    List of stacked reactions. Also shows total reaction count and a button
    to add/remove reactions.
*/
export function MBStackedReactions({
  reactions,
  align,
  selected,
  onToggleReaction,
  pickerBottomMargin,
  anchorName,
  messageId,
}: MBStackedReactionsProps) {
  const reactionButtonAnchorName = `--reaction-button-stacked-${messageId}`;

  const reactionTypes = useMemo(
    () => Array.from(new Set(reactions.map(reaction => reaction.type))),
    [reactions],
  );

  if (isEmpty(reactionTypes)) {
    return null;
  }

  return (
    <View
      row={align === 'right' ? 'reverse' : true}
      wrap
      centerVertically
      testID="stacked-reactions"
      style={{ anchorName }}
    >
      {reactionTypes.map(type => (
        <MBStackedReaction key={type} type={type} reactions={reactions} selected={selected} />
      ))}
      <MBReactionButtonWithPicker
        anchorName={reactionButtonAnchorName}
        selected={selected}
        onToggleReaction={onToggleReaction}
        pickerBottomMargin={pickerBottomMargin}
      />
    </View>
  );
}

function MBStackedReaction({
  type,
  reactions,
  selected,
}: {
  type: API.ReactionType;
  reactions: API.ReactionFragment[];
  selected: API.ReactionType[];
}) {
  const count = reactions.filter(reaction => reaction.type === type).length;
  const emoji = getReactionEmoji(type);

  return (
    <MBView
      center
      row
      paddingHorizontal={1}
      style={[Styles.stackedReaction, selected.includes(type) && Styles.stackedReactionSelected]}
    >
      <MBText style={Styles.stackedReactionEmoji}>{emoji}</MBText>
      <View paddingLeft={0.5}>
        <MBTextOverlineBold color={Colors.v2.gray30}>{count}</MBTextOverlineBold>
      </View>
    </MBView>
  );
}
